import React, { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Table, Tag, Select, Button, Modal, DatePicker, message, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';

interface OtpDeliveryLogRow {
  id: string;
  channel: 'EMAIL' | 'SMS';
  destination: string;
  purpose: string;
  status: 'SENT' | 'FAILED';
  provider: string;
  providerStatus?: string | null;
  providerRef?: string | null;
  errorMessage?: string | null;
  jobId?: string | null;
  attempt: number;
  createdAt: string;
}

const statusColors: Record<string, string> = {
  SENT: '#52c41a',
  FAILED: '#ff4d4f',
};

const channelColors: Record<string, string> = {
  EMAIL: '#1677ff',
  SMS: '#722ed1',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const defaultPurgeDate = () => dayjs().subtract(30, 'day');

export const OtpDeliveryLogList: React.FC = () => {
  const [channelFilter, setChannelFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDate, setPurgeDate] = useState<Dayjs>(defaultPurgeDate);
  const [purging, setPurging] = useState(false);
  const invalidate = useInvalidate();

  const { tableProps } = useTable<OtpDeliveryLogRow>({
    resource: 'otp-delivery-logs',
    filters: {
      permanent: [
        ...(channelFilter ? [{ field: 'channel', operator: 'eq' as const, value: channelFilter }] : []),
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const openPurgeModal = () => {
    setPurgeDate(defaultPurgeDate());
    setPurgeOpen(true);
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const { data } = await axiosInstance.delete(`${API_BASE_URL}/admin/otp-delivery-logs/purge`, {
        params: { beforeDate: purgeDate.startOf('day').toISOString() },
      });
      message.success(`Deleted ${data.deletedCount ?? 0} log(s)`);
      setPurgeOpen(false);
      invalidate({ resource: 'otp-delivery-logs', invalidates: ['list'] });
    } catch {
      message.error('Failed to purge logs');
    } finally {
      setPurging(false);
    }
  };

  return (
    <>
      <List
        resource="otp-delivery-logs"
        canCreate={false}
        headerButtons={({ defaultButtons }) => (
          <>
            {defaultButtons}
            <Tooltip title="Purge logs">
              <Button type="default" icon={<DeleteOutlined />} danger onClick={openPurgeModal} />
            </Tooltip>
          </>
        )}
      >
        <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
          <Select
            placeholder="Channel"
            allowClear
            style={{ width: 140 }}
            onChange={(value) => setChannelFilter(value)}
            options={[
              { label: 'Email', value: 'EMAIL' },
              { label: 'SMS', value: 'SMS' },
            ]}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 140 }}
            onChange={(value) => setStatusFilter(value)}
            options={[
              { label: 'Sent', value: 'SENT' },
              { label: 'Failed', value: 'FAILED' },
            ]}
          />
        </div>
        <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
          <Table.Column
            dataIndex="createdAt"
            title="Time"
            width={180}
            render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
          />
          <Table.Column
            dataIndex="channel"
            title="Channel"
            width={100}
            render={(channel: string) => (
              <Tag style={tagStyle(channelColors[channel] ?? '#898989')}>{channel}</Tag>
            )}
          />
          <Table.Column dataIndex="destination" title="Destination" width={200} />
          <Table.Column dataIndex="purpose" title="Purpose" width={160} />
          <Table.Column
            dataIndex="status"
            title="Status"
            width={110}
            render={(status: string) => (
              <Tag style={tagStyle(statusColors[status] ?? '#898989')}>{status}</Tag>
            )}
          />
          <Table.Column dataIndex="provider" title="Provider" width={100} />
          <Table.Column
            dataIndex="providerStatus"
            title="Provider Status"
            width={120}
            render={(value: string) => value || '—'}
          />
          <Table.Column dataIndex="attempt" title="Attempt" width={80} />
          <Table.Column
            dataIndex="errorMessage"
            title="Error"
            render={(msg: string) => (msg ? (msg.length > 60 ? `${msg.slice(0, 60)}...` : msg) : '—')}
          />
        </Table>
      </List>

      <Modal
        title="Purge OTP Delivery Logs"
        open={purgeOpen}
        onCancel={() => setPurgeOpen(false)}
        onOk={handlePurge}
        confirmLoading={purging}
        okText="Purge"
        okButtonProps={{ danger: true }}
      >
        <p style={{ marginBottom: 12 }}>Delete all logs created before this date:</p>
        <DatePicker
          value={purgeDate}
          onChange={(value) => value && setPurgeDate(value)}
          style={{ width: '100%' }}
          disabledDate={(current) => !!current && current > dayjs().endOf('day')}
        />
      </Modal>
    </>
  );
};
